import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TransportManagementProfileItemBriefService } from './transport-management-profile-item-brief.service';
import { TransportManagementProfileDetails, TransportManagementProfileModel } from '../models';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import {
  TransportManagementProfileItemBriefClone,
  TransportManagementProfileItemBriefDelete,
  TransportManagementProfileItemBriefInitialState,
  TransportManagementProfileItemBriefLoad,
  TransportManagementProfileItemBriefSave,
} from './transport-management-profile-item-brief.actions';
import { TransportManagementProfileItemService } from '../item/transport-management-profile-item.service';
import { Error, CustomHttpErrorResponse, CatalogSearchCriteria } from '../../../../../common';
import { CatalogSearchCriteriaFacade, CatalogSearchCriteriaType } from '../../../catalog-search-criteria';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportManagementProfileItemBriefStateModel extends TransportManagementProfileDetails {
  loaded: boolean;
}

const initialState: TransportManagementProfileItemBriefStateModel = {
  ...new TransportManagementProfileDetails(),
  loaded: false,
};

@State<TransportManagementProfileItemBriefStateModel>({
  name: 'transportManagementProfileItemBrief',
  defaults: initialState,
})
@Injectable()
export class TransportManagementProfileItemBriefState {
  constructor(
    private transportManagementProfileItemBriefService: TransportManagementProfileItemBriefService,
    private transportManagementProfileItemService: TransportManagementProfileItemService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
    private catalogSearchCriteriaFacade: CatalogSearchCriteriaFacade,
  ) {}

  @Selector()
  static getProfileId(state: TransportManagementProfileItemBriefStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(state: TransportManagementProfileItemBriefStateModel): TransportManagementProfileItemBriefStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: TransportManagementProfileItemBriefStateModel): boolean {
    return state.loaded;
  }

  @Action(TransportManagementProfileItemBriefLoad)
  getTransportManagementProfileItemBrief(
    ctx: StateContext<TransportManagementProfileItemBriefStateModel>,
    { payload }: TransportManagementProfileItemBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementProfileItemService.getTransportManagementProfile(payload).pipe(
      map((transportManagementProfile: TransportManagementProfileModel) => {
        ctx.patchState({ ...transportManagementProfile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemBriefSave)
  saveTransportManagementProfileItemBrief(
    ctx: StateContext<TransportManagementProfileItemBriefStateModel>,
    { payload }: TransportManagementProfileItemBriefSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementProfileItemBriefService.saveTransportManagementProfile(payload).pipe(
      withLatestFrom(
        this.catalogSearchCriteriaFacade.getSavedSearchCriteriaByCatalogType(
          CatalogSearchCriteriaType.TransportManagement,
        ),
      ),
      map(([updatedProfile, searchCriteria]: [TransportManagementProfileDetails, CatalogSearchCriteria]) => {
        if (updatedProfile.id === searchCriteria.id) {
          this.catalogSearchCriteriaFacade.changeSearchCriteriaByCatalogType(
            { id: updatedProfile.id, name: updatedProfile.name },
            CatalogSearchCriteriaType.TransportManagement,
          );
        }
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.transportManagementProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemBriefClone)
  cloneTransportManagementProfileItemBrief(
    ctx: StateContext<TransportManagementProfileItemBriefStateModel>,
    { payload }: TransportManagementProfileItemBriefClone,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementProfileItemBriefService.cloneTransportManagementProfile(payload).pipe(
      map(() => {
        this.notificationService.success('api.transportManagementProfile.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/transport-management-profiles');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemBriefDelete)
  deleteTransportManagementProfileItemBrief(
    ctx: StateContext<TransportManagementProfileItemBriefStateModel>,
    { payload }: TransportManagementProfileItemBriefDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.transportManagementProfileItemBriefService.deleteTransportManagementProfile(payload.profileId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.transportManagementProfile.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/transport-management-profiles');
        return ctx.dispatch(new TransportManagementProfileItemBriefInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemBriefInitialState)
  setInitialState(ctx: StateContext<TransportManagementProfileItemBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
